import { askAiWithSystemInstruction } from 'utils/ask-ai-with-company-fallback';
import type {
  WhatsappEditTemplate,
  WhatsappOriginalTemplateComponent,
  WhatsappTemplate,
  WhatsappTemplateCategory,
} from 'types/templates';
import {
  AI_MARKETING_OPT_OUT_QUICK_REPLY_TEXT,
  type ExistingTemplateForAiContext,
  type GenerateManualMetadataResult,
  type GenerateSmartTemplateAiResult,
  type ManualTemplateMetadataPayload,
  type SmartTemplateAiPayload,
} from './smartTemplateAiTypes';


/** Default when no model is stored on the connection (aligned with Integrations UI). */
export const GEMINI_MODEL_FALLBACK = 'gemini-2.5-flash';

/** Meta-style English template name: lowercase, digits, underscores; no leading/trailing/consecutive underscores. */
export const SMART_TEMPLATE_NAME_EN_REGEX = /^[a-z0-9]+(_[a-z0-9]+)*$/;

export function isValidSmartTemplateNameEn(name: string): boolean {
  const t = name.trim();
  if (!t || t.length > 512) return false;
  return SMART_TEMPLATE_NAME_EN_REGEX.test(t);
}

/** English placeholder while user drafts content manually; valid Meta-style pattern. */
export const MANUAL_METADATA_PLACEHOLDER_NAME_EN = 'draft_metadata_placeholder';

export function buildExistingTemplatesForAiPrompt(templates: WhatsappTemplate[]): ExistingTemplateForAiContext[] {
  return templates
    .map((t) => {
      const heRaw = t.hebrewName?.hebrewTemaplateName ?? t.hebrewName?.HebrewTemaplateName ?? '';
      return {
        english_name: (t.name || '').trim(),
        hebrew_display_name: String(heRaw).trim(),
        category: t.category,
        language: String((t as { langugage?: string }).langugage || '').trim(),
      };
    })
    .filter((row) => row.english_name.length > 0 || row.hebrew_display_name.length > 0);
}

function normalizeMetadataLanguage(l: unknown): string | undefined {
  if (typeof l !== 'string') return undefined;
  const t = l.trim().toLowerCase();
  const allowed = new Set(['he', 'en', 'ar', 'ru', 'es']);
  if (allowed.has(t)) return t;
  if (t === 'iw') return 'he';
  return undefined;
}

function normalizeManualMetadataPayload(parsed: Record<string, unknown>): ManualTemplateMetadataPayload | null {
  const template_name_en =
    (typeof parsed.template_name_en === 'string' ? parsed.template_name_en.trim() : '') ||
    (typeof parsed.name_en === 'string' ? parsed.name_en.trim() : '');
  const template_name_he =
    (typeof parsed.template_name_he === 'string' ? parsed.template_name_he.trim() : '') ||
    (typeof parsed.name_he === 'string' ? parsed.name_he.trim() : '');
  const category = normalizeCategory(parsed.category);
  const language = normalizeMetadataLanguage(parsed.language ?? parsed.lang);
  if (!template_name_en || !isValidSmartTemplateNameEn(template_name_en)) return null;
  if (!template_name_he) return null;
  if (!category) return null;
  if (!language) return null;
  return { template_name_en, template_name_he, category, language };
}

export function serializeTemplateDraftForMetadataAi(t: WhatsappEditTemplate): Record<string, unknown> {
  return {
    editor_note:
      'User composed message content manually. Name fields may be placeholders until AI fills Hebrew + English names.',
    category: t.category,
    language: t.language,
    niceName: t.niceName,
    englishName: t.name,
    components: t.components.map((c: WhatsappOriginalTemplateComponent) => {
      if (c.type === 'HEADER') {
        return { type: 'HEADER', format: c.format, text: c.text, example: c.example };
      }
      if (c.type === 'BODY') {
        return { type: 'BODY', text: c.text, example: c.example };
      }
      if (c.type === 'FOOTER') {
        return { type: 'FOOTER', text: c.text };
      }
      if (c.type === 'BUTTONS') {
        return {
          type: 'BUTTONS',
          buttons: (c.buttons || []).map((b) => ({
            type: b.type,
            text: (b as { text?: string }).text,
            url: (b as { url?: string | null }).url,
            phone_number: (b as { phone_number?: string }).phone_number,
          })),
        };
      }
      return { type: (c as { type: string }).type };
    }),
  };
}

const MANUAL_TEMPLATE_METADATA_SYSTEM_PROMPT = `You are a WhatsApp Business Cloud API template naming expert (Meta / Facebook policies).
You receive JSON with:
- "template_draft": structured template the user composed manually (header/body/footer/buttons text and structure).
- "existing_templates": templates already in this WABA — each has english_name (technical lowercase identifier), hebrew_display_name, category, language.

Return ONLY a single JSON object — no markdown fences, no commentary, no text outside JSON:
{
  "template_name_en": string,
  "template_name_he": string,
  "category": "MARKETING" | "UTILITY" | "AUTHENTICATION",
  "language": "he" | "en" | "ar" | "ru" | "es"
}

Rules:
- template_name_en MUST match ^[a-z0-9]+(_[a-z0-9]+)*$ (lowercase Latin, digits, underscores only; no leading/trailing underscore; no "__"). Max length 512. Must comply with Meta internal template name rules.
- template_name_en MUST NOT duplicate any existing_templates[].english_name (compare case-insensitively).
- template_name_he MUST NOT duplicate any existing_templates[].hebrew_display_name (compare after trim; Hebrew is case-sensitive).
- Infer naming style (prefixes, wording, length, parallel Hebrew/English pairing) from existing_templates so the new names fit the organization. If existing_templates is empty, still produce clear, professional names aligned with the draft content.
- category: MARKETING for promotional content; UTILITY for transactional updates; AUTHENTICATION only if the draft is clearly OTP/verification-code style.
- language: primary language of the BODY text, one of he | en | ar | ru | es only.
- Output exactly these four keys. Do not include body_text, buttons, or other fields.`;

export async function generateManualTemplateMetadataAI(params: {
  draft: Record<string, unknown>;
  existingTemplates: ExistingTemplateForAiContext[];
  modelId?: string;
}): Promise<GenerateManualMetadataResult> {
  const userMessage = JSON.stringify({
    template_draft: params.draft,
    existing_templates: params.existingTemplates,
  });

  const res = await askAiWithSystemInstruction({
    systemInstruction: MANUAL_TEMPLATE_METADATA_SYSTEM_PROMPT,
    userMessage,
    model: params.modelId,
    fallbackModel: GEMINI_MODEL_FALLBACK,
  });

  if (!res.ok) {
    return { ok: false, error: res.error };
  }

  let parsed: Record<string, unknown>;
  try {
    const jsonText = extractJsonObjectText(res.text);
    parsed = JSON.parse(jsonText) as Record<string, unknown>;
  } catch {
    return { ok: false, error: 'המודל לא החזיר JSON תקין. נסו שוב.' };
  }

  const payload = normalizeManualMetadataPayload(parsed);
  if (!payload) {
    return {
      ok: false,
      error: 'התשובה חסרה שדות תקינים (שמות, קטגוריה או שפה) או שפורמט השם באנגלית אינו תקין.',
    };
  }

  const exEn = new Set(
    params.existingTemplates.map((t) => t.english_name.toLowerCase()).filter(Boolean),
  );
  const exHe = new Set(
    params.existingTemplates.map((t) => t.hebrew_display_name.replace(/\s+/g, ' ').trim()).filter(Boolean),
  );

  if (exEn.has(payload.template_name_en.toLowerCase())) {
    return {
      ok: false,
      error: 'השם באנגלית שהמודל הציע כבר קיים אצל תבנית אחרת. נסו שוב או ערכו ידנית.',
    };
  }
  const heNorm = payload.template_name_he.replace(/\s+/g, ' ').trim();
  if (exHe.has(heNorm)) {
    return {
      ok: false,
      error: 'שם התבנית בעברית שהמודל הציע כבר קיים אצל תבנית אחרת. נסו שוב או ערכו ידנית.',
    };
  }

  return { ok: true, payload, rawText: res.text };
}

/** Converts `{1}` style placeholders to WhatsApp `{{1}}` where needed. */
export function normalizeWhatsappBodyPlaceholders(text: string): string {
  return text.replace(/\{(?!\{)(\d+)\}/g, '{{$1}}');
}

function extractJsonObjectText(raw: string): string {
  const trimmed = raw.trim();
  const fence = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fence?.[1]) {
    return fence[1].trim();
  }
  const start = trimmed.indexOf('{');
  const end = trimmed.lastIndexOf('}');
  if (start !== -1 && end > start) {
    return trimmed.slice(start, end + 1);
  }
  return trimmed;
}

function normalizeCategory(c: unknown): WhatsappTemplateCategory | undefined {
  if (typeof c !== 'string') return undefined;
  const u = c.toUpperCase();
  if (u === 'UTILITY' || u === 'MARKETING' || u === 'AUTHENTICATION') {
    return u as WhatsappTemplateCategory;
  }
  return undefined;
}

function sortButtonsQrThenUrl<T extends { type: string }>(buttons: T[]): T[] {
  const qr = buttons.filter((b) => b.type === 'QUICK_REPLY');
  const url = buttons.filter((b) => b.type === 'URL');
  return [...qr, ...url] as T[];
}

/** Sample URL for dynamic suffix — accept several JSON keys the model may emit. */
function pickUrlVariableExample(o: Record<string, unknown>): string | undefined {
  const keys = [
    'url_variable_example',
    'url_example',
    'variable_example',
    'url_param_example',
    'dynamic_url_example',
  ] as const;
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  const ex = o.example;
  if (Array.isArray(ex) && typeof ex[0] === 'string' && ex[0].trim()) return ex[0].trim();
  if (typeof ex === 'string' && ex.trim()) return ex.trim();
  return undefined;
}

/** Parse body_parameter_examples as object { "1": "…" } or array in first-appearance order of {{n}} in body. */
function parseBodyParameterExamples(
  parsed: Record<string, unknown>,
  bodyText: string,
): Record<string, string> | undefined {
  const raw = parsed.body_parameter_examples;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(raw)) {
      const key = String(k).replace(/[^\d]/g, '') || k;
      if (typeof v === 'string' && v.trim()) out[key] = v.trim();
    }
    return Object.keys(out).length ? out : undefined;
  }
  if (Array.isArray(raw)) {
    const orderedKeys: string[] = [];
    const seen = new Set<string>();
    for (const m of bodyText.matchAll(/\{\{(\d+)\}\}/g)) {
      const n = m[1];
      if (!seen.has(n)) {
        seen.add(n);
        orderedKeys.push(n);
      }
    }
    const out: Record<string, string> = {};
    raw.forEach((v, i) => {
      if (typeof v === 'string' && v.trim() && orderedKeys[i]) {
        out[orderedKeys[i]] = v.trim();
      }
    });
    return Object.keys(out).length ? out : undefined;
  }
  return undefined;
}

function isAiOptOutLikeQuickReplyText(text: string): boolean {
  const t = text.trim().toLowerCase();
  if (t === 'הסר') return true;
  if (t.includes('הסרה') || t.includes('הסר מ')) return true;
  if (t === AI_MARKETING_OPT_OUT_QUICK_REPLY_TEXT.toLowerCase()) return true;
  if (/opt-?out|unsubscribe|unsub/i.test(t)) return true;
  return false;
}

/**
 * For MARKETING: drop any AI-authored opt-out buttons; keep at most 2 quick replies from AI,
 * then append the system opt-out as the last quick reply (before URL buttons). Max 3 quick replies total.
 */
function finalizeMarketingPayload(base: SmartTemplateAiPayload): SmartTemplateAiPayload {
  const cat = base.category ?? 'MARKETING';
  if (cat !== 'MARKETING') {
    return {
      ...base,
      category: cat,
      marketingOptOutAppended: false,
    };
  }

  const stripped = base.buttons.filter(
    (b) => !(b.type === 'QUICK_REPLY' && isAiOptOutLikeQuickReplyText(b.text)),
  );
  const sorted = sortButtonsQrThenUrl(stripped);
  const qr = sorted.filter((b) => b.type === 'QUICK_REPLY');
  const url = sorted.filter((b) => b.type === 'URL');
  const qrFromAi = qr.slice(0, 2);

  const finalButtons: SmartTemplateAiPayload['buttons'] = [
    ...qrFromAi,
    { type: 'QUICK_REPLY', text: AI_MARKETING_OPT_OUT_QUICK_REPLY_TEXT },
    ...url,
  ];

  return {
    ...base,
    category: 'MARKETING',
    buttons: finalButtons,
    marketingOptOutAppended: true,
  };
}

function normalizePayload(parsed: Record<string, unknown>): SmartTemplateAiPayload | null {
  const template_name_en =
    (typeof parsed.template_name_en === 'string' ? parsed.template_name_en.trim() : '') ||
    (typeof parsed.name_en === 'string' ? parsed.name_en.trim() : '');
  const template_name_he =
    (typeof parsed.template_name_he === 'string' ? parsed.template_name_he.trim() : '') ||
    (typeof parsed.name_he === 'string' ? parsed.name_he.trim() : '');
  const body_raw =
    (typeof parsed.body_text === 'string' ? parsed.body_text.trim() : '') ||
    (typeof parsed.body === 'string' ? parsed.body.trim() : '');

  if (!template_name_en || !isValidSmartTemplateNameEn(template_name_en)) {
    return null;
  }
  if (!template_name_he) {
    return null;
  }
  if (!body_raw) {
    return null;
  }

  const body_text = normalizeWhatsappBodyPlaceholders(body_raw);

  const rawButtons = parsed.buttons;
  if (!Array.isArray(rawButtons) || rawButtons.length === 0) {
    return null;
  }

  const buttons: SmartTemplateAiPayload['buttons'] = [];
  for (const b of rawButtons) {
    if (!b || typeof b !== 'object') continue;
    const o = b as Record<string, unknown>;
    const type = o.type === 'QUICK_REPLY' || o.type === 'URL' ? o.type : null;
    const text = typeof o.text === 'string' ? o.text.trim() : '';
    if (!type || !text) continue;
    if (type === 'URL') {
      const urlRaw = typeof o.url === 'string' ? o.url.trim() : '';
      if (!urlRaw) continue;
      const url = normalizeWhatsappBodyPlaceholders(urlRaw);
      const url_variable_example = pickUrlVariableExample(o);
      buttons.push({
        type: 'URL',
        text,
        url,
        ...(url_variable_example ? { url_variable_example } : {}),
      });
    } else {
      buttons.push({ type: 'QUICK_REPLY', text });
    }
  }

  const hasQrOrUrl = buttons.some((x) => x.type === 'QUICK_REPLY' || x.type === 'URL');
  if (!hasQrOrUrl) {
    return null;
  }

  const category = normalizeCategory(parsed.category) ?? 'MARKETING';

  const body_parameter_examples = parseBodyParameterExamples(parsed, body_text);

  const base: SmartTemplateAiPayload = {
    template_name_en,
    template_name_he,
    body_text,
    ...(body_parameter_examples ? { body_parameter_examples } : {}),
    buttons,
    category,
  };

  return finalizeMarketingPayload(base);
}

const SMART_TEMPLATE_SYSTEM_PROMPT = `You are a WhatsApp Business template copy assistant for Meta (Facebook) Cloud API.
Your response must be ONLY a single valid JSON object — no markdown fences, no commentary, no text before or after the JSON.

When the user message includes a JSON array labeled with existing WhatsApp templates for this account (field existing_templates or prose "Existing templates"):
- template_name_en MUST NOT duplicate any existing english_name (case-insensitive).
- template_name_he MUST NOT duplicate any existing hebrew_display_name (trimmed; Hebrew is case-sensitive).
- Match naming style (prefixes, length, Hebrew/English pairing) observed in those rows.

Category:
- If the message is promotional, sales, discounts, offers, campaigns, or newsletters, set "category": "MARKETING".
- If purely transactional (order status, OTP, account alerts), use "UTILITY" when appropriate.

Schema (strict):
{
  "template_name_en": string (required): lowercase a-z, digits, underscores only; pattern ^[a-z0-9]+(_[a-z0-9]+)*$
  "template_name_he": string (required): Hebrew display name for the template.
  "category": "MARKETING" | "UTILITY" | "AUTHENTICATION" (optional; use MARKETING for marketing content).
  "body_text": string (required): compliant with Meta policies; persuasive but respectful; may use {{1}}, {{2}} or {1}, {2} for variables.
  "body_parameter_examples": object (required whenever body_text contains placeholders): keys "1","2",… mapping to realistic sample values in Hebrew for each variable (e.g. first name, company name, order id). The app will inject these as Meta sample values — the user must not fill them manually.
  "buttons": array (required, min 1): only QUICK_REPLY and/or URL. For URL buttons with {{n}} in the url, include "url_variable_example": string with a realistic sample for that dynamic segment.
}

CRITICAL — do NOT add unsubscribe / opt-out quick replies:
- Do NOT create any button whose text is "הסר", "הסרה", "Opt-out", "unsubscribe", or similar. The Berry app will append a compliant marketing opt-out button on the client when category is MARKETING.

Marketing copy:
- Make body_text marketing-oriented and call-to-action where appropriate, still clear and respectful.

Button placement note (for your ordering): put main call-to-action quick replies and URLs first; do not reserve an opt-out slot — the system adds it last for MARKETING templates.

Parameter samples (mandatory when using {{n}} in body_text):
- Always output body_parameter_examples with every variable index used in body_text. Use natural Hebrew samples (names, companies, cities, order numbers).

Output JSON only. Keep body_text under 1024 characters when possible.`;

export async function generateSmartTemplateAI(
  userBrief: string,
  opts?: { modelId?: string; existingTemplates?: ExistingTemplateForAiContext[] },
): Promise<GenerateSmartTemplateAiResult> {
  const brief = userBrief.trim();
  if (!brief) {
    return { ok: false, error: 'נא להזין תיאור לתבנית.' };
  }

  const existing = opts?.existingTemplates?.length
    ? `\n\nexisting_templates (JSON array — avoid duplicate template_name_en / template_name_he; mirror naming patterns):\n${JSON.stringify(opts.existingTemplates)}`
    : '';

  const res = await askAiWithSystemInstruction({
    systemInstruction: SMART_TEMPLATE_SYSTEM_PROMPT,
    userMessage: `${brief}${existing}`,
    model: opts?.modelId,
    fallbackModel: GEMINI_MODEL_FALLBACK,
  });

  if (!res.ok) {
    return { ok: false, error: res.error };
  }

  let parsed: Record<string, unknown>;
  try {
    const jsonText = extractJsonObjectText(res.text);
    parsed = JSON.parse(jsonText) as Record<string, unknown>;
  } catch {
    return { ok: false, error: 'המודל לא החזיר JSON תקין. נסה לנסח מחדש או נסה שוב.' };
  }

  const payload = normalizePayload(parsed);
  if (!payload) {
    return {
      ok: false,
      error:
        'התשובה חסרה שדות נדרשים (template_name_en, template_name_he, body_text, כפתורים תקינים) או שפורמט שם באנגלית אינו תקין.',
    };
  }

  return { ok: true, payload, rawText: res.text };
}
